import { createContext, useEffect, useReducer, useRef } from 'react';
import PropTypes from 'prop-types';
import { Hub } from 'aws-amplify/utils';
import {
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  resendSignUpCode,
  resetPassword,
  signIn,
  signInWithRedirect,
  signOut,
  signUp,
  updatePassword
} from 'aws-amplify/auth';

import { apiGetUser } from '../components/blusensor/BluSensorUserAPI';
import { getMailDomain, getSsoIdentities, storeSsoReturnPath } from '../utils/ssoLogin';

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  ssoError: null
};

// Amplify handles the return from the hosted UI (/callback/login?code=... or ?error=...) as soon as it is
// configured, before any component mounts, and reports a failure only on the Hub - keep it for the callback page.
let hostedUiError = null;
Hub.listen('auth', ({ payload }) => {
  if (payload.event === 'signInWithRedirect_failure') {
    const error = payload.data?.error;
    hostedUiError = error?.message || String(error);
  }
});

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user, ssoError = null } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      ssoError
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null
  }),
  REGISTER: (state) => ({ ...state }),
  VERIFY_CODE: (state) => ({ ...state }),
  RESEND_CODE: (state) => ({ ...state }),
  PASSWORD_RECOVERY: (state) => ({ ...state }),
  PASSWORD_RESET: (state) => ({ ...state }),
  PASSWORD_CHANGE: (state) => ({ ...state }),
};

const reducer = (state, action) => (handlers[action.type]
  ? handlers[action.type](state, action)
  : state);

const AuthContext = createContext({
  ...initialState,
  platform: 'Amplify',
  login: () => Promise.resolve(),
  loginWithProvider: () => Promise.resolve(),
  loginWithCompanyAccount: () => Promise.resolve(),
  sendLoginCode: () => Promise.resolve(),
  loginWithCode: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  verifyCode: () => Promise.resolve(),
  resendCode: () => Promise.resolve(),
  passwordRecovery: () => Promise.resolve(),
  passwordReset: () => Promise.resolve(),
  passwordChange: () => Promise.resolve(),
  reloadUser: () => Promise.resolve()
});


// Claims of the ID token of the signed in user, throws without a session (like currentAuthenticatedUser in Amplify v4)
async function getIdTokenPayload() {
  const { tokens } = await fetchAuthSession();
  if (!tokens?.idToken) {
    throw new Error('The user is not authenticated');
  }
  return tokens.idToken.payload;
}

// User id = Cognito sub, the key the API checks ($ctx.identity.sub). Equal to the username for password users;
// a user who signed up through an identity provider has the username <provider>_<id at the provider>.
function getUserId(idTokenPayload) {
  return idTokenPayload.sub;
}

function getUserPayload(idTokenPayload, user) {

  return ({
    id: getUserId(idTokenPayload),
    sso_identities: getSsoIdentities(idTokenPayload),
    user_type: user.user_type,
    user_status: user.user_status,
    user_role: user.user_role,
    photo: user.photo,
    email: user.email,
    name: user.name,
    //email: userCognito.attributes.email,
    //name: userCognito.attributes.name,
    phone: user.phone,
    country: user.country,
    city: user.city,
    zip: user.zip,
    street: user.street,
    language: user.language,
    customer: user.customer,
    usage_plan: user.usage_plan,
    usage_price: user.usage_price,
    usage_period: user.usage_period,
    usage_expiry: user.usage_expiry,
    usage_contract: user.usage_contract,
    alarm_channel: user.alarm_channel,
    alarm_channel_severity: user.alarm_channel_severity,
    alarm_channel_plan_enabled: user.alarm_channel_plan_enabled,
    alarm_channel_plan: user.alarm_channel_plan,
    alarm_sensor: user.alarm_sensor,
    alarm_battery: user.alarm_battery,
    alarm_connection: user.alarm_connection,
    alarm_hysteresis: user.alarm_hysteresis,
    alarm_hardware: user.alarm_hardware,
    alarm_signal: user.alarm_signal,
    alarm_power: user.alarm_power,
    alarm_firmware: user.alarm_firmware,
    billing_name: user.billing_name,
    billing_company: user.billing_company,
    billing_country: user.billing_country,
    billing_city: user.billing_city,
    billing_street: user.billing_street,
    billing_vat: user.billing_vat,
    billing_currency: user.billing_currency,
    billing_zip: user.billing_zip,
    api_key: user.api_key,

  });
}

export const AuthProvider = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  // mirror the active user id so the cross-tab listener can compare without stale closures
  const currentUserIdRef = useRef(null);
  currentUserIdRef.current = state.isAuthenticated ? state.user?.id ?? null : null;

  // Cognito tokens live in shared localStorage; a sign-in/out in another tab silently
  // swaps the active session here while this tab's UI keeps showing the old user.
  // Detect that swap and reload so the UI and API can never disagree on who is logged in.
  useEffect(() => {
    const handleStorage = async (event) => {
      if (event.key && !event.key.includes('CognitoIdentityServiceProvider')) {
        return;
      }
      try {
        const idTokenPayload = await getIdTokenPayload();
        if (currentUserIdRef.current && getUserId(idTokenPayload) !== currentUserIdRef.current) {
          window.location.reload();
        }
      } catch (error) {
        if (currentUserIdRef.current) {
          window.location.reload();
        }
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    const initialize = async () => {
      try {

        //get user info from AWS Cognito
        const idTokenPayload = await getIdTokenPayload();

        let user = await apiGetUser(getUserId(idTokenPayload));

        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: true,
            user: getUserPayload(idTokenPayload, user)
          }
        });
      } catch (error) {
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
            ssoError: hostedUiError
          }
        });
      }
    };

    initialize();
  }, []);

  const completeLogin = async () => {
    const idTokenPayload = await getIdTokenPayload();
    let user = await apiGetUser(getUserId(idTokenPayload));

    dispatch({
      type: 'LOGIN',
      payload: {
        isAuthenticated: true,
        user: getUserPayload(idTokenPayload, user)
      }
    });

  };

  // Amplify v6 reports an unconfirmed address as next step, the login page expects the Cognito error
  const assertConfirmed = (nextStep) => {
    if (nextStep.signInStep === 'CONFIRM_SIGN_UP') {
      const error = new Error('User is not confirmed.');
      error.name = 'UserNotConfirmedException';
      throw error;
    }
  };

  // The login page shows when the app could not load the user (e.g. account deleted, no profile) while Amplify still
  // holds that user's tokens; v6 then refuses a new sign-in. Drop the stale local session and sign in again.
  const withoutStaleSession = async (signInCall) => {
    try {
      return await signInCall();
    } catch (err) {
      if (err?.name !== 'UserAlreadyAuthenticatedException') {
        throw err;
      }
      await signOut();
      return await signInCall();
    }
  };

  const login = async (email, password) => {
    const { isSignedIn, nextStep } = await withoutStaleSession(() => signIn({ username: email, password }));

    if (!isSignedIn) {
      assertConfirmed(nextStep);
      console.error(`Unable to login, because step "${nextStep.signInStep}" is mandated and we did not handle this case.`);
      return;
    }

    await completeLogin();
  };

  // One-time code by email (choice-based sign-in): Cognito mails the code, Amplify keeps the sign-in session for
  // loginWithCode. Sending again starts a new sign-in with a new code.
  const sendLoginCode = async (email) => {
    const { isSignedIn, nextStep } = await withoutStaleSession(() => signIn({
      username: email,
      options: { authFlowType: 'USER_AUTH', preferredChallenge: 'EMAIL_OTP' }
    }));

    if (isSignedIn) {
      await completeLogin();
      return;
    }
    assertConfirmed(nextStep);
    if (nextStep.signInStep !== 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE') {
      throw new Error(`Unexpected sign-in step "${nextStep.signInStep}" instead of the code by email`);
    }
  };

  const loginWithCode = async (code) => {
    const { isSignedIn, nextStep } = await confirmSignIn({ challengeResponse: code.trim() });

    if (!isSignedIn) {
      throw new Error(`Unexpected sign-in step "${nextStep.signInStep}" after the code by email`);
    }

    await completeLogin();
  };

  // Redirects to the hosted UI of the identity provider, the sign-in completes on SSO_CALLBACK_LOGIN
  const loginWithProvider = async (provider) => {
    storeSsoReturnPath();
    await withoutStaleSession(() => signInWithRedirect({ provider: { custom: provider } }));
  };

  // Company account: the mail domains are identifiers of the company's identity provider in Cognito,
  // the hosted UI gets the domain as idp_identifier and picks the provider itself
  const loginWithCompanyAccount = async (email) => {
    storeSsoReturnPath();
    await withoutStaleSession(() => signInWithRedirect({ provider: { idpIdentifier: getMailDomain(email) } }));
  };

  const logout = async () => {
    await signOut();
    dispatch({
      type: 'LOGOUT'
    });
  };

  const register = async (email, password, name) => {
    await signUp({
      username: email,
      password,
      options: { userAttributes: { email, name } }
    });
    dispatch({
      type: 'REGISTER'
    });
  };

  const verifyCode = async (username, code) => {
    await confirmSignUp({ username, confirmationCode: code });
    dispatch({
      type: 'VERIFY_CODE'
    });
  };

  const resendCode = async (username) => {
    await resendSignUpCode({ username });
    dispatch({
      type: 'RESEND_CODE'
    });
  };

  const passwordRecovery = async (username) => {
    await resetPassword({ username });
    dispatch({
      type: 'PASSWORD_RECOVERY'
    });
  };

  const passwordReset = async (username, code, newPassword) => {
    await confirmResetPassword({ username, confirmationCode: code, newPassword });
    dispatch({
      type: 'PASSWORD_RESET'
    });
  };

  const passwordChange = async (oldPassword, newPassword) => {
    await updatePassword({ oldPassword, newPassword });
    dispatch({
      type: 'PASSWORD_CHANGE'
    });
  };

  const reloadUser = async () => {

    const idTokenPayload = await getIdTokenPayload();
    let user = await apiGetUser(getUserId(idTokenPayload));

    dispatch({
      type: 'INITIALIZE',
      payload: {
        isAuthenticated: true,
        user: getUserPayload(idTokenPayload, user)
      }
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        platform: 'Amplify',
        login,
        loginWithProvider,
        loginWithCompanyAccount,
        sendLoginCode,
        loginWithCode,
        logout,
        register,
        verifyCode,
        resendCode,
        passwordRecovery,
        passwordReset,
        passwordChange,
        reloadUser
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default AuthContext;
